<template>
  <div class="contact-page">
    <div class="container">
      <!-- Page Header -->
      <div class="contact-header">
        <h1>Contact Us</h1>
        <p>We're here to help! Reach out to us through any of the channels below.</p>
      </div>

      <!-- Main Content Grid: Form + Sidebar -->
      <div class="contact-main-grid">
        <!-- Contact Form (Left) -->
        <div class="contact-form-section">
          <h2>Send Us a Message</h2>
          <p class="form-subtitle">Fill out the form below and we'll get back to you within 24 hours.</p>
          
          <div v-if="successMessage" class="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <i class="fas fa-check-circle"></i>
            {{ successMessage }}
          </div>

          <form @submit.prevent="submitForm" class="contact-form">
            <!-- Honeypot field -->
            <div class="form-group" style="position: absolute; left: -9999px; opacity: 0;">
              <label for="website">Website</label>
              <input v-model="form.website" type="text" id="website" name="website" tabindex="-1" autocomplete="off">
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="name">Your Name *</label>
                <input v-model="form.name" type="text" id="name" placeholder="Enter your name" required :disabled="submitting">
              </div>
              <div class="form-group">
                <label for="email">Email Address *</label>
                <input v-model="form.email" type="email" id="email" placeholder="Enter your email" required :disabled="submitting">
              </div>
            </div>
            
            <div class="form-group">
              <label for="subject">Subject</label>
              <input v-model="form.subject" type="text" id="subject" placeholder="What is this about?" :disabled="submitting">
            </div>
            
            <div class="form-group">
              <label for="message">Message *</label>
              <textarea v-model="form.message" id="message" rows="6" placeholder="Write your message here..." required :disabled="submitting"></textarea>
            </div>
            
            <button type="submit" class="btn-submit" :disabled="submitting">
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
              {{ submitting ? 'Sending...' : 'Send Message' }}
            </button>
          </form>
        </div>

        <!-- Contact Info Sidebar (Right) -->
        <div class="contact-sidebar">
          <!-- Phone -->
          <div class="contact-info-card">
            <div class="info-icon">
              <i class="fas fa-phone-alt"></i>
            </div>
            <div class="info-content">
              <h4>Call Us</h4>
              <a href="tel:07011110034">07011110034</a>
              <span class="info-note">Mon-Sat, 8AM - 6PM</span>
            </div>
          </div>

          <!-- WhatsApp -->
          <div class="contact-info-card">
            <div class="info-icon whatsapp">
              <i class="fab fa-whatsapp"></i>
            </div>
            <div class="info-content">
              <h4>WhatsApp</h4>
              <a href="https://wa.me/2347011110034" target="_blank">Chat with us</a>
              <span class="info-note">Quick responses</span>
            </div>
          </div>

          <!-- Help Center Link -->
          <a href="/help-center" class="help-center-link">
            <i class="fas fa-question-circle"></i>
            <span>Visit Help Center</span>
            <i class="fas fa-chevron-right"></i>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const form = ref({
  name: '',
  email: '',
  subject: '',
  message: '',
  website: '' // honeypot field
})

const submitting = ref(false)
const successMessage = ref('')

const submitForm = async () => {
  submitting.value = true
  successMessage.value = ''
  
  try {
    const formData = new FormData()
    formData.append('name', form.value.name)
    formData.append('email', form.value.email)
    formData.append('subject', form.value.subject)
    formData.append('message', form.value.message)
    formData.append('website', form.value.website) // honeypot
    
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    
    const response = await fetch('/contact', {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': token,
        'Accept': 'application/json'
      },
      body: formData
    })
    
    if (response.ok) {
      successMessage.value = 'Thank you for contacting us! We will get back to you soon.'
      form.value = { name: '', email: '', subject: '', message: '' }
    } else {
      const data = await response.json()
      alert(data.message || 'Something went wrong. Please try again.')
    }
  } catch (error) {
    alert('Something went wrong. Please try again.')
  } finally {
    submitting.value = false
  }
}
</script>

